// Structure — company org tree. Model where work happens; a person placed on a
// unit reaches everything beneath it. Tree render + expand/collapse + select and
// the pan/zoom Chart view are live; drag-to-reparent, details panel and the
// remaining header actions come later.

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Button, Card, Icon, Input, SegmentedControl } from '@/components/common';
import { useGetCompanyStructureQuery } from '@/api/endpoints/structureApi';
import type { OrgUnit } from '@/mocks/structure';
import {
  useCreateTagMutation,
  useDeleteTagMutation,
  useGetTagsQuery,
  useUpdateTagMutation,
} from '@/api/endpoints/tagsApi';
import { useGetLevelsQuery, useUpdateLevelsMutation } from '@/api/endpoints/levelsApi';
import useModuleAccess, { useCanManageStructureTags } from '@/hooks/useModuleAccess';
import CompanyNode from './tree/CompanyNode';
import UnitTree from './tree/UnitTree';
import OrgChart from './chart/OrgChart';
import TagsModal, { type TagDraft } from './TagsModal';
import LevelsModal, { type LevelDraft } from './LevelsModal';
import CreateUnitDrawer from './CreateUnitDrawer';
import UnitDetailsDrawer from './UnitDetailsDrawer';
import StructureFilter from './StructureFilter';
import { type UnitDrawerMode } from './unitForm';

// Depth-first list of every unit under (and including) the roots.
const flattenUnits = (units: OrgUnit[]): OrgUnit[] =>
  units.flatMap((u) => [u, ...flattenUnits(u.children ?? [])]);

// Keep only nodes in `keep`, preserving the parent→child hierarchy.
const pruneTree = (unit: OrgUnit, keep: Set<string>): OrgUnit => ({
  ...unit,
  children: (unit.children ?? []).filter((c) => keep.has(c.id)).map((c) => pruneTree(c, keep)),
});

const StructurePage = () => {
  const { t } = useTranslation();
  const [view, setView] = useState<'tree' | 'chart'>('chart');
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [tagsOpen, setTagsOpen] = useState(false);
  const [savingTags, setSavingTags] = useState(false);
  const [levelsOpen, setLevelsOpen] = useState(false);
  const [drawer, setDrawer] = useState<UnitDrawerMode | null>(null);
  const [search, setSearch] = useState('');
  const [nodeIds, setNodeIds] = useState<Set<string>>(new Set());
  const [tagIds, setTagIds] = useState<Set<string>>(new Set());

  const {
    data: roots = [],
    isLoading: treeLoading,
    isError: treeError,
  } = useGetCompanyStructureQuery();
  const root = roots[0];
  // Default: only the top level — branches show collapsed (expandedIds starts empty).

  const { data: tags = [] } = useGetTagsQuery();
  const [createTag] = useCreateTagMutation();
  const [updateTag] = useUpdateTagMutation();
  const [deleteTag] = useDeleteTagMutation();

  const { data: levels = [] } = useGetLevelsQuery();
  const [updateLevels, { isLoading: savingLevels }] = useUpdateLevelsMutation();

  // Write affordances are hidden when the verb is missing; edit surfaces render
  // read-only instead. `company_structure` grants no `delete` — the module has
  // no node-delete affordance to gate.
  const { canCreate, canEdit } = useModuleAccess('company_structure');
  const canManageTags = useCanManageStructureTags();

  const toggle = (id: string) =>
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const toggleSet = (setState: typeof setNodeIds) => (id: string) =>
    setState((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  // Flat unit list (for the filter's node checklist) + a parent lookup for
  // walking ancestors.
  const allUnits = useMemo(() => flattenUnits(roots), [roots]);
  const parentById = useMemo(() => {
    const map = new Map<string, string>();
    const walk = (u: OrgUnit, parentId?: string) => {
      if (parentId) map.set(u.id, parentId);
      u.children?.forEach((c) => walk(c, u.id));
    };
    roots.forEach((r) => walk(r));
    return map;
  }, [roots]);

  const filtering = !!search.trim() || nodeIds.size > 0 || tagIds.size > 0;

  // Directly-matched units: search narrows by name, ANDed with the union of
  // checked nodes/tags.
  const matched = useMemo(() => {
    if (!filtering) return [];
    const q = search.trim().toLowerCase();
    const hasPicks = nodeIds.size > 0 || tagIds.size > 0;
    return allUnits.filter(
      (u) =>
        (!q || u.name.toLowerCase().includes(q)) &&
        (!hasPicks || nodeIds.has(u.id) || (u.tags ?? []).some((tag) => tagIds.has(tag.id))),
    );
  }, [allUnits, filtering, search, nodeIds, tagIds]);

  // Shown in hierarchy form: each match keeps its whole subtree (children) and
  // its chain of ancestors (recurring parents) up to the company root.
  const keptIds = useMemo(() => {
    const keep = new Set<string>();
    const addSubtree = (u: OrgUnit) => {
      keep.add(u.id);
      u.children?.forEach(addSubtree);
    };
    for (const m of matched) {
      addSubtree(m);
      for (let p = parentById.get(m.id); p; p = parentById.get(p)) keep.add(p);
    }
    return keep;
  }, [matched, parentById]);

  // The tree pruned to the kept set (root is always kept when there are matches,
  // since ancestors are included), rendered in each view's own layout.
  const prunedRoot = useMemo(
    () => (filtering && root && keptIds.has(root.id) ? pruneTree(root, keptIds) : root),
    [filtering, root, keptIds],
  );

  // Selecting a unit highlights it and opens its details drawer.
  const selectUnit = (id: string) => {
    setSelectedId(id);
    setDrawer({ type: 'details', id });
  };

  // Closing the drawer clears the selection so no row stays highlighted.
  const closeDrawer = () => {
    setDrawer(null);
    setSelectedId(null);
  };

  // After a create, reveal the new unit by expanding its parent (the tree
  // itself refetches via the `Structure` invalidation).
  const onUnitCreated = (parentId: string) => setExpandedIds((prev) => new Set(prev).add(parentId));

  // Diff the modal draft against the fetched tags and fire the matching
  // create / rename / delete mutations; the `Tags` cache invalidation refetches.
  const saveTags = async (draft: TagDraft[]) => {
    const byId = new Map(tags.map((tag) => [tag.id, tag.title]));
    const draftIds = new Set(draft.filter((d) => d.id).map((d) => d.id));
    const creates = draft.filter((d) => !d.id);
    const updates = draft.filter((d) => d.id && byId.get(d.id) !== d.title);
    const deletes = tags.filter((tag) => !draftIds.has(tag.id));

    setSavingTags(true);
    try {
      await Promise.all([
        ...creates.map((d) => createTag({ title: d.title }).unwrap()),
        ...updates.map((d) => updateTag({ id: d.id as string, title: d.title }).unwrap()),
        ...deletes.map((tag) => deleteTag(tag.id).unwrap()),
      ]);
      setTagsOpen(false);
      toast.success(t('structure_tags_saved'));
    } catch {
      toast.error(t('error_generic'));
    } finally {
      setSavingTags(false);
    }
  };

  // Bulk-save the level draft; the `Levels` invalidation refetches so the
  // tree/chart re-render with the new names/icons automatically.
  const saveLevels = async (draft: LevelDraft[]) => {
    try {
      await updateLevels({
        levels: draft.map((row) => ({
          id: row.id,
          name: row.name,
          icon: row.icon,
          sqlColumn: row.sqlColumn,
          sqlColumnEnabled: row.sqlColumnEnabled,
        })),
      }).unwrap();
      setLevelsOpen(false);
      toast.success(t('levels_saved'));
    } catch {
      toast.error(t('error_generic'));
    }
  };

  return (
    <div className={`structure-page${view === 'chart' ? ' structure-page--fill' : ''}`}>
      <header className='structure-page__header'>
        <div className='structure-page__heading'>
          <h1 className='structure-page__title'>{t('structure_title')}</h1>
          <p className='structure-page__subtitle'>{t('structure_subtitle')}</p>
        </div>
        <div className='structure-page__actions'>
          {/* Tags manages the tag LIBRARY, so it rides on the `tags` extra.
              Attaching an existing tag to a unit is ordinary `edit` and lives in
              the unit form. Levels edits the level taxonomy — structure editing,
              and there is no dedicated slug for it. */}
          {canManageTags && (
            <Button variant='secondary' iconStart='tag' onClick={() => setTagsOpen(true)}>
              {t('structure_action_tags')}
            </Button>
          )}
          {canEdit && (
            <Button
              variant='secondary'
              iconStart='sliders-horizontal'
              onClick={() => setLevelsOpen(true)}
            >
              {t('structure_action_levels')}
            </Button>
          )}
          {canCreate && (
            <Button
              iconStart='plus'
              onClick={() => setDrawer({ type: 'create', parentId: root?.id })}
            >
              {t('structure_action_add')}
            </Button>
          )}
        </div>
      </header>

      <Card padded={false} className='structure-page__panel'>
        <div className='structure-page__toolbar'>
          <h2 className='structure-page__panel-title'>{t('structure_card_title')}</h2>
          <div className='structure-page__toolbar-end'>
            <Input
              size='sm'
              className='structure-page__search'
              iconStart='search'
              placeholder={t('structure_search_placeholder')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <StructureFilter
              units={allUnits.filter((u) => u.level !== 0)}
              tags={tags}
              nodeIds={nodeIds}
              tagIds={tagIds}
              onToggleNode={toggleSet(setNodeIds)}
              onToggleTag={toggleSet(setTagIds)}
            />
            <SegmentedControl
              aria-label={t('structure_card_title')}
              value={view}
              onChange={(v) => setView(v as 'tree' | 'chart')}
              options={[
                { value: 'chart', label: t('structure_view_chart') },
                { value: 'tree', label: t('structure_view_tree') },
              ]}
            />
          </div>
        </div>

        <div className='structure-page__body'>
          {treeLoading ? (
            <div className='structure-page__status'>{t('text_loading')}</div>
          ) : treeError ? (
            <div className='structure-page__status'>{t('error_generic')}</div>
          ) : !prunedRoot ? (
            <div className='structure-page__status'>{t('structure_empty')}</div>
          ) : filtering && matched.length === 0 ? (
            <div className='structure-page__status'>{t('structure_no_results')}</div>
          ) : view === 'tree' ? (
            <>
              <CompanyNode unit={prunedRoot} />
              <UnitTree
                root={prunedRoot}
                // While filtering, expand every kept branch so all matches show.
                expandedIds={filtering ? keptIds : expandedIds}
                selectedId={selectedId}
                onToggle={toggle}
                onSelect={selectUnit}
              />
            </>
          ) : (
            <OrgChart
              // Remount on filter-set change so the chart refits to the results.
              key={filtering ? `f:${keptIds.size}` : 'full'}
              root={prunedRoot}
              selectedId={selectedId}
              onSelect={selectUnit}
            />
          )}
        </div>

        {view === 'tree' && root && (
          <div className='structure-page__footer'>
            <Icon name='move' size={16} />
            {t('structure_footer')}
          </div>
        )}
      </Card>

      <TagsModal
        open={tagsOpen}
        tags={tags}
        saving={savingTags}
        onClose={() => setTagsOpen(false)}
        onSave={saveTags}
      />

      <LevelsModal
        open={levelsOpen}
        levels={levels}
        saving={savingLevels}
        onClose={() => setLevelsOpen(false)}
        onSave={saveLevels}
      />

      <CreateUnitDrawer
        open={drawer?.type === 'create'}
        parentId={drawer?.type === 'create' ? drawer.parentId : undefined}
        onClose={closeDrawer}
        onCreated={onUnitCreated}
      />

      <UnitDetailsDrawer
        open={drawer?.type === 'details'}
        id={drawer?.type === 'details' ? drawer.id : undefined}
        onClose={closeDrawer}
        onAddChild={(parentId) => setDrawer({ type: 'create', parentId })}
      />
    </div>
  );
};

export default StructurePage;
